import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useTranslation } from 'react-i18next';
import ChartPlaceholder from './ChartPlaceholder';
import { createRotatedLabelDualAxisConfig } from './chartConfigUtils';

interface SeasonalityData {
    readonly period: string;
    readonly avgContracts: number;
    readonly avgRevenue: number;
    readonly avgRedemptions: number;
    readonly peakIndicator: number;
}

interface SeasonalityChartProps {
    readonly data: readonly SeasonalityData[];
    readonly loading?: boolean;
    readonly currency?: 'EUR' | 'LEI';
}

function createSeasonalityTooltipFormatter(
    data: readonly SeasonalityData[],
    getMonthName: (monthNum: string) => string,
    getPeakLabel: (indicator: number) => string,
    t: (key: string, defaultValue: string) => string,
): Highcharts.TooltipFormatterCallbackFunction {
    return function () {
        const points = this.points;
        if (!points) return '';
        let tooltip = `<b>${this.x}</b><br/>`;
        points.forEach(point => {
            tooltip += `${point.series.name}: <b>${Highcharts.numberFormat(Number(point.y), 0, ',', ' ')}</b><br/>`;
        });
        const monthName = String(this.x);
        const dataPoint = data.find(d => getMonthName(d.period) === monthName);
        if (dataPoint) {
            tooltip += `<br/>${t('statistics.advanced.seasonIndicator', 'Season')}: <b>${getPeakLabel(dataPoint.peakIndicator)}</b>`;
        }
        return tooltip;
    };
}

function SeasonalityChart({
    data,
    loading = false,
    currency = 'EUR',
}: SeasonalityChartProps): JSX.Element {
    const { t } = useTranslation();

    if (loading || data.length === 0) {
        return <ChartPlaceholder loading={loading} hasData={data.length > 0} />;
    }

    // Get translated month names from period (month number 01-12)
    const getMonthName = (monthNum: string): string => {
        const monthIndex = parseInt(monthNum, 10) - 1;
        // Use translation keys for month names
        const monthKeys = [
            'months.january',
            'months.february',
            'months.march',
            'months.april',
            'months.may',
            'months.june',
            'months.july',
            'months.august',
            'months.september',
            'months.october',
            'months.november',
            'months.december',
        ];
        const defaultNames = [
            'January',
            'February',
            'March',
            'April',
            'May',
            'June',
            'July',
            'August',
            'September',
            'October',
            'November',
            'December',
        ];
        if (monthIndex >= 0 && monthIndex < 12) {
            return t(monthKeys[monthIndex], defaultNames[monthIndex]);
        }
        return monthNum;
    };

    // Get translated peak labels
    const getPeakLabel = (indicator: number): string => {
        const labels = [
            t('statistics.advanced.peakLabels.low', 'Low'),
            t('statistics.advanced.peakLabels.belowAvg', 'Below Avg'),
            t('statistics.advanced.peakLabels.average', 'Average'),
            t('statistics.advanced.peakLabels.aboveAvg', 'Above Avg'),
            t('statistics.advanced.peakLabels.peak', 'Peak'),
        ];
        return labels[Math.max(0, Math.min(indicator - 1, 4))];
    };

    const categories = data.map(d => getMonthName(d.period));

    // Color coding based on peak indicator
    const getBarColor = (peakIndicator: number): string => {
        switch (peakIndicator) {
            case 5:
                return '#52c41a'; // Green - peak
            case 4:
                return '#73d13d'; // Light green
            case 3:
                return '#faad14'; // Yellow - average
            case 2:
                return '#ff7a45'; // Orange
            default:
                return '#ff4d4f'; // Red - low
        }
    };

    const currencyLabel = currency === 'LEI' ? 'RON' : 'EUR';

    const base = createRotatedLabelDualAxisConfig(
        categories,
        t('statistics.advanced.month', 'Month'),
        t('statistics.advanced.contracts', 'Contracts'),
        t('statistics.advanced.revenue', 'Revenue') + ` (${currencyLabel})`,
    );

    const options: Highcharts.Options = {
        ...base,
        tooltip: {
            shared: true,
            formatter: createSeasonalityTooltipFormatter(data, getMonthName, getPeakLabel, t),
        },
        plotOptions: {
            column: {
                grouping: true,
                borderWidth: 0,
            },
        },
        series: [
            {
                type: 'column',
                name: t('statistics.advanced.avgContracts', 'Avg Contracts'),
                data: data.map(d => ({
                    y: d.avgContracts,
                    color: getBarColor(d.peakIndicator),
                })),
                yAxis: 0,
            },
            {
                type: 'spline',
                name: t('statistics.advanced.avgRevenue', 'Avg Revenue'),
                data: data.map(d => d.avgRevenue),
                color: '#1890ff',
                yAxis: 1,
                marker: {
                    enabled: true,
                    radius: 4,
                },
            },
        ],
        credits: {
            enabled: false,
        },
    };

    return <HighchartsReact highcharts={Highcharts} options={options} />;
}

export default SeasonalityChart;
